
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MasterLicenseLayout from '@/components/masterlicense/MasterLicenseLayout';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import BannerCreationMethodStep from '@/components/admin/marketing/creation/BannerCreationMethodStep';
import BannerSizeSelectionStep from '@/components/admin/marketing/creation/BannerSizeSelectionStep';
import BannerTemplateSelectionStep from '@/components/admin/marketing/creation/BannerTemplateSelectionStep';
import BannerEditorStep from '@/components/admin/marketing/creation/BannerEditorStep';
import BannerSetReviewStep from '@/components/admin/marketing/creation/BannerSetReviewStep';
import BannerRejectedFixStep from '@/components/admin/marketing/creation/BannerRejectedFixStep';
import { BannerCreationDraft, BannerTemplateFormat } from '@/types/bannerTemplate';
import { BannerSize } from '@/types/banner';
import { loadBannerDraft, saveBannerDraft } from '@/utils/banner/bannerCore';

const BannerCreationPage = () => {
  const navigate = useNavigate();
  
  // Load draft from localStorage or initialize with defaults
  const [creationState, setCreationState] = useState<BannerCreationDraft>(() => {
    const saved = loadBannerDraft<BannerCreationDraft>('banner_creation_draft');
    return saved || {
      step: 'size-selection',
      useAI: false
    };
  });

  // Keep track of rejected banners that need fixing
  const [rejectedBanners, setRejectedBanners] = useState<BannerTemplateFormat[]>([]);
  
  // Track if we're using AI for rejected banners
  const [useAI, setUseAI] = useState(false);

  const handleBack = () => {
    let previousStep: BannerCreationDraft['step'];
    
    switch (creationState.step) {
      case 'template-selection':
        previousStep = 'size-selection';
        break;
      case 'banner-editing':
        previousStep = creationState.selectedTemplateId ? 'template-selection' : 'size-selection';
        break;
      case 'set-review':
        previousStep = 'banner-editing';
        break;
      case 'fix-rejected':
        previousStep = 'set-review';
        break;
      default:
        // If we're at the first step, navigate back to templates page
        navigate('/master/marketing/banner-templates');
        return;
    }
    
    setCreationState(prev => {
      const updated = { ...prev, step: previousStep };
      saveBannerDraft('banner_creation_draft', updated);
      return updated;
    });
  };

  const handleSelectCreationMethod = (isTemplate: boolean) => {
    if (isTemplate) {
      setCreationState(prev => {
        const updated = { ...prev, step: 'template-selection' as const };
        saveBannerDraft('banner_creation_draft', updated);
        return updated;
      });
    } else {
      setCreationState(prev => {
        const updated = { ...prev, step: 'size-selection' as const, selectedTemplateId: undefined };
        saveBannerDraft('banner_creation_draft', updated);
        return updated;
      });
    }
  };

  const handleSelectSize = (size: BannerSize) => {
    setCreationState(prev => {
      const updated = { ...prev, step: 'banner-editing' as const, selectedSize: size };
      saveBannerDraft('banner_creation_draft', updated);
      return updated;
    });
  };

  const handleSelectTemplate = (templateId: string, templateSetId: string) => {
    setCreationState(prev => {
      const updated = {
        ...prev,
        step: 'banner-editing' as const,
        selectedTemplateId: templateId,
        selectedTemplateSetId: templateSetId
      };
      saveBannerDraft('banner_creation_draft', updated);
      return updated;
    });
  };

  const handleFinishEditing = () => {
    setCreationState(prev => {
      const updated = { ...prev, step: 'set-review' as const };
      saveBannerDraft('banner_creation_draft', updated);
      return updated;
    });
  };

  const handleProcessRejected = (rejectedFormats: BannerTemplateFormat[]) => {
    setRejectedBanners(rejectedFormats);
    setCreationState(prev => {
      const updated = { 
        ...prev, 
        step: 'fix-rejected' as const,
        rejectedBanners: rejectedFormats,
        currentRejectedIndex: 0
      };
      saveBannerDraft('banner_creation_draft', updated);
      return updated;
    });
  };

  const handleCompleteWorkflow = () => {
    // Clean up draft data
    localStorage.removeItem('banner_creation_draft');
    // Navigate back to templates page
    navigate('/master/marketing/banner-templates', { 
      state: { bannerCreated: true }
    });
  };

  const handleSetUseAI = (useAIValue: boolean) => {
    setUseAI(useAIValue);
    setCreationState(prev => {
      const updated = { ...prev, useAI: useAIValue };
      saveBannerDraft('banner_creation_draft', updated);
      return updated;
    });
  };

  // Handle AI usage for rejected banners
  const handleUseAIForRejected = (rejectedFormats: BannerTemplateFormat[]) => {
    handleSetUseAI(true);
    handleProcessRejected(rejectedFormats);
  };

  // Mock logout function for MasterLicenseLayout
  const handleLogout = () => {
    console.log("Logout action would happen here");
  };

  // Render the current step
  const renderStep = () => {
    switch (creationState.step) {
      case 'size-selection':
        return (
          <BannerCreationMethodStep 
            onSelectMethod={handleSelectCreationMethod}
          />
        );
      case 'template-selection':
        return (
          <BannerTemplateSelectionStep 
            onSelectTemplate={handleSelectTemplate}
          />
        );
      case 'banner-editing':
        return (
          <BannerEditorStep 
            selectedSize={creationState.selectedSize}
            selectedTemplateId={creationState.selectedTemplateId}
            onFinish={handleFinishEditing}
          />
        );
      case 'set-review':
        return (
          <BannerSetReviewStep 
            selectedTemplateId={creationState.selectedTemplateId}
            onApproveAll={handleCompleteWorkflow}
            onProcessRejected={handleProcessRejected}
            onUseAI={handleUseAIForRejected}
          />
        );
      case 'fix-rejected':
        return (
          <BannerRejectedFixStep 
            rejectedBanners={rejectedBanners}
            useAI={useAI}
            onComplete={handleCompleteWorkflow}
          />
        );
      default:
        return <div>Unknown step</div>;
    }
  };

  const getStepTitle = () => {
    switch (creationState.step) {
      case 'size-selection': 
        return 'Step 1: Choose Banner Creation Method';
      case 'template-selection': 
        return 'Step 2: Browse Templates';
      case 'banner-editing': 
        return 'Step 3: Edit Template';
      case 'set-review': 
        return 'Step 4: Preview & Approval';
      case 'fix-rejected': 
        return 'Step 5: Fix Rejected Templates';
      default: 
        return 'Create Banner';
    }
  };

  return (
    <MasterLicenseLayout activeTab="banner-creation" onLogout={handleLogout}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBack}
            className="mr-2"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <h1 className="text-2xl font-semibold">{getStepTitle()}</h1>
        </div>

        <div className="py-6">
          {renderStep()}
        </div>
      </div>
    </MasterLicenseLayout>
  );
};

export default BannerCreationPage;
